import { supabase } from '../lib/supabase';

export const companyAuthService = {
  /**
   * Sign in company user and verify company association
   */
  async signIn(email, password, companySlug = null) {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({ 
        email, 
        password 
      });

      if (error) throw error;

      // Fetch user profile to verify company association
      const { data: profile, error: profileError } = await supabase?.from('user_profiles')?.select('id, email, full_name, role, company_id, companies:company_id(id, name, slug)')?.eq('id', data?.user?.id)?.single();

      if (profileError) throw profileError;

      // Verify user has company_admin role
      if (profile?.role !== 'company_admin') {
        await supabase?.auth?.signOut();
        throw new Error('Access denied. This login is for company administrators only.');
      }

      // Verify user has company association
      if (!profile?.company_id) {
        await supabase?.auth?.signOut();
        throw new Error('No company associated with this account.');
      }

      // If specific company slug provided, verify user belongs to that company
      if (companySlug && profile?.companies?.slug !== companySlug) {
        await supabase?.auth?.signOut();
        throw new Error('You do not have access to this company.');
      }

      return { 
        data: {
          user: data?.user,
          profile: {
            id: profile?.id,
            email: profile?.email,
            fullName: profile?.full_name,
            role: profile?.role,
            companyId: profile?.company_id,
            company: profile?.companies ? {
              id: profile?.companies?.id,
              name: profile?.companies?.name,
              slug: profile?.companies?.slug
            } : null
          }
        }, 
        error: null 
      };
    } catch (error) {
      return { 
        data: null, 
        error: { message: error?.message || 'Network error. Please try again.' } 
      };
    }
  },

  /**
   * Get company details by slug
   */
  async getCompanyBySlug(slug) {
    try {
      const { data, error } = await supabase?.from('companies')?.select('id, name, slug, logo, primary_color, secondary_color, is_active')?.eq('slug', slug)?.eq('is_active', true)?.single();

      if (error) throw error;

      return {
        data: {
          id: data?.id,
          name: data?.name,
          slug: data?.slug,
          logo: data?.logo,
          primaryColor: data?.primary_color,
          secondaryColor: data?.secondary_color,
          isActive: data?.is_active
        },
        error: null
      };
    } catch (error) {
      return { 
        data: null, 
        error: { message: 'Company not found or inactive.' } 
      };
    }
  },

  /**
   * Verify current user is company admin
   */
  async verifyCompanyAdmin() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: profile, error } = await supabase?.from('user_profiles')?.select('role, company_id')?.eq('id', user?.id)?.single();

      if (error) throw error;

      return {
        isCompanyAdmin: profile?.role === 'company_admin',
        hasCompany: !!profile?.company_id,
        companyId: profile?.company_id
      };
    } catch (error) {
      return {
        isCompanyAdmin: false,
        hasCompany: false,
        companyId: null
      };
    }
  }
};